import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { localStorage } from '@change-corgi/core/storage';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { DEFAULT_GOAL, MAX_GOAL, MIN_GOAL } from '../../constants';

import { saveShareGoal } from './api';

const SHARING_GOAL_STORAGE_KEY_PREFIX = 'sharing-goal-';

function getStorageKey(petitionId: string): string {
	return `${SHARING_GOAL_STORAGE_KEY_PREFIX}${petitionId}`;
}

function readStoredGoal(petitionId: string): number {
	if (!localStorage.hasLocalStorage()) return DEFAULT_GOAL;

	const raw = localStorage.getItem(getStorageKey(petitionId));
	if (raw == null) return DEFAULT_GOAL;

	const value = Number(raw);
	if (!Number.isInteger(value) || value < MIN_GOAL || value > MAX_GOAL) return DEFAULT_GOAL;

	return value;
}

function writeStoredGoal(petitionId: string, value: number): void {
	localStorage.setItem(getStorageKey(petitionId), String(value));
}

type GoalMotivationEntry = {
	minGoal: number;
	emoji: string;
	message: string;
};

function getGoalMotivation(goal: number, config: GoalMotivationEntry[]) {
	let result = config[0];
	for (const entry of config) {
		if (goal >= entry.minGoal) result = entry;
	}
	return result;
}

type UseSetGoalStepOptions = {
	petitionId: string;
	userId: string | undefined;
	initialShareGoal: number | null;
};

export function useSetGoalStep({ petitionId, userId, initialShareGoal }: UseSetGoalStepOptions) {
	const { translate } = useI18n();
	const utilityContext = useUtilityContext();
	const { errorReporter } = utilityContext;
	const track = useTracking();

	const [goalValue, setGoalValueState] = useState(initialShareGoal ?? DEFAULT_GOAL);
	const [prevPetitionId, setPrevPetitionId] = useState(petitionId);
	const [prevInitialShareGoal, setPrevInitialShareGoal] = useState(initialShareGoal);

	// Render-phase state sync (React docs: "Adjusting state when a prop changes").
	// Without this, if the flow is reused across a petition switch (same instance, new petitionId),
	// goalValue would stay on the previous petition's value and saveGoal would PUT it under the new petitionId.
	if (petitionId !== prevPetitionId || initialShareGoal !== prevInitialShareGoal) {
		setPrevPetitionId(petitionId);
		setPrevInitialShareGoal(initialShareGoal);
		setGoalValueState(initialShareGoal ?? DEFAULT_GOAL);
	}

	const setGoalValue = useCallback(
		(value: number | ((prev: number) => number)) => {
			setGoalValueState((prev) => {
				const next = typeof value === 'function' ? value(prev) : value;
				writeStoredGoal(petitionId, next);
				return next;
			});
		},
		[petitionId],
	);

	// The explicit `value` avoids a stale `goalValue` closure when onChangeEnd fires in the
	// same event as the final onChange (before React has committed the state update).
	const trackSliderChange = useCallback(
		(value: number) => {
			void track('psf_share_goal_slider_change', { goal_value: value, petition_id: petitionId });
		},
		[track, petitionId],
	);

	const lastSavedRef = useRef<{ petitionId: string; goalValue: number } | null>(null);

	const { mutateAsync: saveGoal } = useMutation({
		mutationFn: async () => {
			if (!userId) return;
			if (lastSavedRef.current?.petitionId === petitionId && lastSavedRef.current.goalValue === goalValue) return;
			lastSavedRef.current = { petitionId, goalValue };
			try {
				await saveShareGoal({ petitionId, goalValue }, utilityContext);
			} catch (error) {
				lastSavedRef.current = null;
				void errorReporter.report({
					error: new Error('Failed to save sharing goal to server', { cause: error }),
					params: { petitionId, goalValue },
				});
			}
		},
	});

	// Keep initial render deterministic for SSR/hydration: read localStorage only post-mount.
	// Seeds localStorage from the server-prefetched value when available; otherwise reads any
	// stored goal back into state. Does not save to the server — saves are gated to share actions.
	useEffect(() => {
		if (initialShareGoal != null) {
			writeStoredGoal(petitionId, initialShareGoal);
			return;
		}
		const storedGoal = readStoredGoal(petitionId);
		// eslint-disable-next-line react-hooks/set-state-in-effect -- reading localStorage during render causes an SSR/hydration mismatch: the server always returns DEFAULT_GOAL but the client may have a stored value, producing diverging output on hydration.
		setGoalValueState(storedGoal);
	}, [petitionId, initialShareGoal]);

	const { emoji, message } = getGoalMotivation(goalValue, [
		{ minGoal: 1, emoji: '🌱', message: translate('corgi.psf.share.sharing_goal.motivation_1') },
		{ minGoal: 5, emoji: '⭐', message: translate('corgi.psf.share.sharing_goal.motivation_5') },
		{ minGoal: 10, emoji: '💪', message: translate('corgi.psf.share.sharing_goal.motivation_10') },
		{ minGoal: 20, emoji: '🚀', message: translate('corgi.psf.share.sharing_goal.motivation_20') },
	]);

	return { goalValue, setGoalValue, trackSliderChange, saveGoal, emoji, message };
}
